
import { computed, defineComponent, onBeforeUnmount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'

// module components
import InformationHandler from '@/modules/SecurityCheck/components/InformationHandler/index.vue'
import PersonInformation from '@/modules/SecurityCheck/components/PersonInformation/index.vue'
import CompanyInformation from '@/modules/SecurityCheck/components/CompanyInformation/index.vue'
import AutoInformation from '@/modules/SecurityCheck/components/AutoInformation/index.vue'
import History from '@/modules/SecurityCheck/components/History/index.vue'
import BeforeCheckedInformation from '@/modules/SecurityCheck/components/BeforeCheckedInformation/index.vue'

// types
import {
    SecurityGetPersonInformationResponseT,
    SecurityGetCompanyInformationResponseT,
    SecurityCompanyInformation,
    SecurityPersonInformation,
    InvalidDataResponseT,
    InProgressDataResponseT,
    CompanyDataResponseT,
    PersonDataResponseT,
    SecurityGetAutoInformationResponseT,
    AutoDataResponseT,
    SecurityAutoInformation,
} from '@/core/types/security.types'

// realisation
import { security } from '@/core/realisations/security.realisation'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

// validations
import useVuelidate from '@vuelidate/core'
import { requiredIf, required } from '@vuelidate/validators'

const TIMER_INTERVAL = 7 * 1000 // 7s

const COMPANY_CHECKUP_TYPE = 1
const PERSON_CHECKUP_TYPE = 2
const AUTO_CHECKUP_TYPE = 3

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        checkbox: Checkbox,
        modal: Modal,
        TheTextarea,
        //
        'information-handler': InformationHandler,
        'person-information': PersonInformation,
        'company-information': CompanyInformation,
        'auto-information': AutoInformation,
        History,
        'before-checked-information': BeforeCheckedInformation,
    },
    setup() {
        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const before_checked_state = reactive({
            is_loading: false,
            is_checked: false,
            is_request_completed: false,
            is_accepted: null as boolean | null,
            comment: null as string | null,
        })

        const resetBeforeCheckedState = () => {
            before_checked_state.is_checked = false
            before_checked_state.is_request_completed = false
            before_checked_state.is_accepted = null
            before_checked_state.comment = null
        }

        const getBeforeCheckedInformation = async (identifier: string, type: number) => {
            resetBeforeCheckedState()

            try {
                before_checked_state.is_loading = true

                const response: any = await security.getCheckupStatus({ identifier, type })

                before_checked_state.is_checked = true

                if (response.data.isAccepted === false) {
                    before_checked_state.is_accepted = false
                    before_checked_state.comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    before_checked_state.is_accepted = true
                } else {
                    before_checked_state.is_accepted = null
                }
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                before_checked_state.is_checked = false
            } finally {
                before_checked_state.is_loading = false
                before_checked_state.is_request_completed = true
            }
        }

        const state = reactive({
            is_person_data: false,
            is_company_data: true,
            is_auto_data: false,
            //
            is_manually: false,
            //
            person_iin: null as string | null,
            company_bin: null as string | null,
            auto_number: null as string | null,
            auto_technical_passport: null as string | null,
            //
            is_person_loading: false,
            is_person_data_loading: false,
            is_person_in_progress: false,
            is_person_not_found: false,
            is_person_decline_loading: false,
            is_person_confirm_loading: false,
            person_update_timer: null as number | null,
            //
            is_company_loading: false,
            is_company_data_loading: false,
            is_company_in_progress: false,
            is_company_not_found: false,
            is_company_decline_loading: false,
            is_company_confirm_loading: false,
            company_update_timer: null as number | null,
            //
            is_auto_loading: false,
            is_auto_data_loading: false,
            is_auto_in_progress: false,
            is_auto_not_found: false,
            is_auto_decline_loading: false,
            is_auto_confirm_loading: false,
            auto_update_timer: null as number | null,
            //
            person_information: null as SecurityPersonInformation | null,
            company_information: null as SecurityCompanyInformation | null,
            auto_information: null as SecurityAutoInformation | null,
            //
            not_found_message: null as string | null,
            //
            auto_number_temp: null as string | null,
            auto_technical_passport_temp: null as string | null,
        })

        const state_form$ = useVuelidate(
            {
                person_iin: {
                    required: requiredIf(() => state.is_person_data),
                },
                company_bin: {
                    required: requiredIf(() => state.is_company_data),
                },
                auto_number: {
                    required: requiredIf(() => state.is_auto_data),
                },
                auto_technical_passport: {
                    required: requiredIf(() => state.is_auto_data),
                },
            },
            state
        )

        const createPersonUpdateTimer = () => {
            // @ts-ignore
            state.person_update_timer = setInterval(getPersonInformation, TIMER_INTERVAL)
        }

        const removePersonUpdateTimer = () => {
            // @ts-ignore
            clearInterval(state.person_update_timer)
            state.person_update_timer = null
        }

        const createCompanyUpdateTimer = () => {
            // @ts-ignore
            state.company_update_timer = setInterval(getCompanyInformation, TIMER_INTERVAL)
        }

        const removeCompanyUpdateTimer = () => {
            // @ts-ignore
            clearInterval(state.company_update_timer)
            state.company_update_timer = null
        }

        const createAutoUpdateTimer = () => {
            // @ts-ignore
            state.auto_update_timer = setInterval(getAutoInformation, TIMER_INTERVAL)
        }

        const removeAutoUpdateTimer = () => {
            // @ts-ignore
            clearInterval(state.auto_update_timer)
            state.auto_update_timer = null
        }

        const onByCompany = (value: boolean) => {
            resetPersonState()
            resetCompanyState()
            resetAutoState()
            resetBeforeCheckedState()

            if (value) {
                state.is_person_data = false
                state.is_auto_data = false
            }

            state_form$.value.$reset()
        }

        const onByPerson = (value: boolean) => {
            resetPersonState()
            resetCompanyState()
            resetAutoState()
            resetBeforeCheckedState()

            if (value) {
                state.is_company_data = false
                state.is_auto_data = false
            }

            state_form$.value.$reset()
        }

        const onByAuto = (value: boolean) => {
            resetPersonState()
            resetCompanyState()
            resetAutoState()
            resetBeforeCheckedState()

            if (value) {
                state.is_company_data = false
                state.is_person_data = false
            }
        }

        // const isPersonNotFound = (response: SecurityGetPersonInformationResponseT): boolean => {
        //     const invalid_response = response as InvalidDataResponseT

        //     const key = invalid_response.key
        //     const message = invalid_response.message

        //     if (key && key === 'not_found') {
        //         state.not_found_message = message
        //         return true
        //     }

        //     state.not_found_message = null
        //     return false
        // }

        // const isPersonInProgress = (response: SecurityGetPersonInformationResponseT): boolean => {
        //     const key = (response as InProgressDataResponseT).key

        //     if (key && key === 'in_progress') return true
        //     return false
        // }

        const resetPersonState = () => {
            state.is_person_not_found = false
            state.person_information = null
        }

        const getPersonInformation = async () => {
            try {
                state.is_person_data_loading = true

                // const person_iin = state.person_iin as string
                // const response = await security.getPersonInformation(person_iin)

                // state.is_person_not_found = isPersonNotFound(response)
                // state.is_person_in_progress = isPersonInProgress(response)

                if (state.is_person_not_found) {
                    removePersonUpdateTimer()
                    return
                }

                if (state.is_person_in_progress) {
                    if (!state.person_update_timer) {
                        createPersonUpdateTimer()
                    }
                    return
                }

                removePersonUpdateTimer()

                // АВТОЗАПОЛНЕНИЕ ADATA
                // state.person_information = (response as PersonDataResponseT).data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации о физ. лице произошла ошибка',
                })
            } finally {
                state.is_person_data_loading = false
            }
        }

        const isCompanyNotFound = (response: SecurityGetCompanyInformationResponseT): boolean => {
            const invalid_response = response as InvalidDataResponseT

            const key = invalid_response.key
            const message = invalid_response.message

            if (key && key === 'not_found') {
                state.not_found_message = message
                return true
            }

            state.not_found_message = null
            return false
        }

        const isCompanyInProgress = (response: SecurityGetCompanyInformationResponseT): boolean => {
            const key = (response as InProgressDataResponseT).key

            if (key && key === 'in_progress') return true
            return false
        }

        const resetCompanyState = () => {
            state.is_company_not_found = false
            state.company_information = null
        }

        const getCompanyInformation = async () => {
            try {
                state.is_company_data_loading = true

                // const company_bin = state.company_bin as string
                // const response = await security.getCompanyInformation(company_bin)

                // state.is_company_not_found = isCompanyNotFound(response)
                // state.is_company_in_progress = isCompanyInProgress(response)

                if (state.is_company_not_found) {
                    removeCompanyUpdateTimer()
                    return
                }

                if (state.is_company_in_progress) {
                    if (!state.company_update_timer) {
                        createCompanyUpdateTimer()
                    }
                    return
                }

                removeCompanyUpdateTimer()

                // АВТОЗАПОЛНЕНИЕ ADATA
                // state.company_information = (response as CompanyDataResponseT).data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации о компании произошла ошибка',
                })
            } finally {
                state.is_company_data_loading = false
            }
        }

        const isAutoNotFound = (response: SecurityGetAutoInformationResponseT): boolean => {
            const invalid_response = response as InvalidDataResponseT

            const key = invalid_response.key
            const message = invalid_response.message

            if (key && key === 'not_found') {
                state.not_found_message = message
                return true
            }

            state.not_found_message = null
            return false
        }

        const isAutoInProgress = (response: SecurityGetAutoInformationResponseT): boolean => {
            const key = (response as InProgressDataResponseT).key

            if (key && key === 'in_progress') return true
            return false
        }

        const resetAutoState = () => {
            state.is_auto_not_found = false
            state.auto_information = null

            state.auto_number_temp = null
            state.auto_technical_passport_temp = null
        }

        const getAutoInformation = async () => {
            try {
                state.is_auto_data_loading = true

                // const number = state.auto_number as string
                // const response = await security.getAutoInformation(number)

                // state.is_auto_not_found = isAutoNotFound(response)
                // state.is_auto_in_progress = isAutoInProgress(response)

                if (state.is_auto_not_found) {
                    removeAutoUpdateTimer()
                    return
                }

                if (state.is_auto_in_progress) {
                    if (!state.auto_update_timer) {
                        createAutoUpdateTimer()
                    }
                    return
                }

                removeAutoUpdateTimer()

                // АВТОЗАПОЛНЕНИЕ ADATA
                // state.auto_information = (response as AutoDataResponseT).data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении информации об авто произошла ошибка',
                })
            } finally {
                state.is_auto_data_loading = false
            }
        }

        const createPersonInformationRequest = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_person_loading = true

                const person_iin = state.person_iin as string

                getBeforeCheckedInformation(person_iin, PERSON_CHECKUP_TYPE)
                if (!state.is_manually) {
                    // await security.createPersonInformationRequest(person_iin)
                    getPersonInformation()
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании запроса на получение информации о физ. лице произошла ошибка',
                })
            } finally {
                state.is_person_loading = false
            }
        }

        const createCompanyInformationRequest = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_company_loading = true

                const company_bin = state.company_bin as string

                getBeforeCheckedInformation(company_bin, COMPANY_CHECKUP_TYPE)
                if (!state.is_manually) {
                    // await security.createCompanyInformationRequest(company_bin)
                    getCompanyInformation()
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании запроса на получение информации о компании произошла ошибка',
                })
            } finally {
                state.is_company_loading = false
            }
        }

        const createAutoInformationRequest = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_company_loading = true

                const number = state.auto_number as string
                const technical_passport = state.auto_technical_passport as string

                getBeforeCheckedInformation(number, AUTO_CHECKUP_TYPE)
                if (!state.is_manually) {
                    // await security.createAutoInformationRequest(number, technical_passport)

                    state.auto_number_temp = number
                    state.auto_technical_passport_temp = technical_passport

                    getAutoInformation()
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании запроса на получение информации об авто произошла ошибка',
                })
            } finally {
                state.is_company_loading = false
            }
        }

        const onCreateInformationRequest = async () => {
            if (
                state.is_person_loading ||
                state.is_company_loading ||
                state.is_auto_loading ||
                state.is_person_data_loading ||
                state.is_company_data_loading ||
                state.is_auto_data_loading
            )
                return

            resetPersonState()
            resetCompanyState()
            resetAutoState()

            if (state.is_person_data) {
                createPersonInformationRequest()
                return
            }

            if (state.is_company_data) {
                createCompanyInformationRequest()
                return
            }

            if (state.is_auto_data) {
                createAutoInformationRequest()
                return
            }
        }

        const openAccept = () => {
            accept_state.comment = null
            accept_state.is_opened = true
        }

        const closeAccept = () => {
            accept_state.is_opened = false
        }

        const onConfirmPerson = async () => {
            try {
                state.is_person_confirm_loading = true

                const identifier = state.person_iin as string
                await security.setCheckupAccept(
                    identifier,
                    PERSON_CHECKUP_TYPE,
                    accept_state.comment
                )

                notifications.notify({ title: 'Физ. лицу разрешено', type: 'success' })
                closeAccept()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При разрешении физ. лицу произошла ошибка',
                })
            } finally {
                state.is_person_confirm_loading = false
            }
        }

        const onConfirmCompany = async () => {
            try {
                state.is_company_confirm_loading = true

                const identifier = state.company_bin as string
                await security.setCheckupAccept(
                    identifier,
                    COMPANY_CHECKUP_TYPE,
                    accept_state.comment
                )

                notifications.notify({ title: 'Компании разрешено', type: 'success' })
                closeAccept()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При разрешении компании произошла ошибка',
                })
            } finally {
                state.is_company_confirm_loading = false
            }
        }

        const onConfirmAuto = async () => {
            try {
                state.is_auto_confirm_loading = true

                const identifier = state.auto_number as string
                await security.setCheckupAccept(identifier, AUTO_CHECKUP_TYPE, accept_state.comment)

                notifications.notify({ title: 'Авто разрешено', type: 'success' })
                closeAccept()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При разрешении авто произошла ошибка' })
            } finally {
                state.is_auto_confirm_loading = false
            }
        }

        const decline_state = reactive({
            is_opened: false,
            comment: null as string | null,
        })

        const decline_form$ = useVuelidate({ comment: { required } }, decline_state)

        const accept_state = reactive({
            is_opened: false,
            comment: null as string | null,
        })

        const openDecline = () => {
            decline_state.comment = null
            decline_form$.value.$reset()
            decline_state.is_opened = true
        }

        const closeDecline = () => {
            decline_state.is_opened = false
        }

        const onDeclinePerson = async () => {
            try {
                state.is_person_decline_loading = true

                const identifier = state.person_iin as string
                const reason = decline_state.comment as string

                await security.setCheckupReject(identifier, PERSON_CHECKUP_TYPE, reason)

                notifications.notify({ title: 'Физ. лицу отказано', type: 'success' })
                closeDecline()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отказе физ. лицу произошла ошибка' })
            } finally {
                state.is_person_decline_loading = false
            }
        }

        const onDeclineCompany = async () => {
            try {
                state.is_company_decline_loading = true

                const identifier = state.company_bin as string
                const reason = decline_state.comment as string

                await security.setCheckupReject(identifier, COMPANY_CHECKUP_TYPE, reason)

                notifications.notify({ title: 'Компании отказано', type: 'success' })
                closeDecline()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отказе компании произошла ошибка' })
            } finally {
                state.is_company_decline_loading = false
            }
        }

        const onDeclineAuto = async () => {
            try {
                state.is_auto_decline_loading = true

                const identifier = state.auto_number as string
                const reason = decline_state.comment as string

                await security.setCheckupReject(identifier, AUTO_CHECKUP_TYPE, reason)

                notifications.notify({ title: 'Авто отказано', type: 'success' })
                closeDecline()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отказе авто произошла ошибка' })
            } finally {
                state.is_auto_decline_loading = false
            }
        }

        const declineManager = () => {
            decline_form$.value.$touch()
            if (decline_form$.value.$invalid) return

            if (state.is_person_data) {
                onDeclinePerson()
                return
            }

            if (state.is_company_data) {
                onDeclineCompany()
                return
            }

            if (state.is_auto_data) {
                onDeclineAuto()
                return
            }
        }

        const acceptManager = () => {
            if (state.is_person_data) {
                onConfirmPerson()
                return
            }

            if (state.is_company_data) {
                onConfirmCompany()
                return
            }

            if (state.is_auto_data) {
                onConfirmAuto()
                return
            }
        }

        //
        const isInformationHandlerRender = computed(() => {
            if (
                state.is_person_in_progress ||
                state.is_person_not_found ||
                state.is_company_in_progress ||
                state.is_company_not_found ||
                state.is_auto_in_progress ||
                state.is_auto_not_found
            ) {
                return true
            }

            return false
        })

        const isPersonInformationRender = computed(() => {
            if (state.is_manually && state.is_person_data) return true

            if (!state.is_person_data) return false
            if (
                state.is_person_loading ||
                state.is_person_in_progress ||
                state.is_person_not_found ||
                !state.person_information
            )
                return false
            return true
        })

        const isCompanyInformationRender = computed(() => {
            if (state.is_manually && state.is_company_data) return true

            if (!state.is_company_data) return false
            if (
                state.is_company_loading ||
                state.is_company_in_progress ||
                state.is_company_not_found ||
                !state.company_information
            )
                return false
            return true
        })

        const isAutoInformationRender = computed(() => {
            if (state.is_manually && state.is_auto_data) return true

            if (!state.is_auto_data) return false
            if (
                state.is_auto_loading ||
                state.is_auto_in_progress ||
                state.is_auto_not_found ||
                !state.auto_information
            )
                return false

            return true
        })

        const isCanGetCompanyInformation = computed(() =>
            havePermissions(['contract-adata-company-view', 'all-root-permission'])
        )
        const isCanGetPersonInformation = computed(() =>
            havePermissions(['contract-adata-person-view', 'all-root-permission'])
        )
        const isCanGetAutoInformation = computed(() =>
            havePermissions(['contract-adata-car-view', 'all-root-permission'])
        )

        const isCanSendCompanyInformationRequest = computed(() =>
            havePermissions(['contract-adata-company-create-bid', 'all-root-permission'])
        )
        const isCanSendPersonInformationRequest = computed(() =>
            havePermissions(['contract-adata-person-create-bid', 'all-root-permission'])
        )
        const isCanSendAutoInformationRequest = computed(() =>
            havePermissions(['contract-adata-car-create-bid', 'all-root-permission'])
        )

        const isCanDecline = computed(() =>
            havePermissions(['contract-checkup-reject', 'all-root-permission'])
        )
        const isCanConfirm = computed(() =>
            havePermissions(['contract-checkup-accept', 'all-root-permission'])
        )

        const isHaveCompanyAccess = computed(
            () => isCanGetCompanyInformation.value && isCanSendCompanyInformationRequest.value
        )
        const isHavePersonAccess = computed(
            () => isCanGetPersonInformation.value && isCanSendPersonInformationRequest.value
        )
        const isHaveAutoAccess = computed(
            () => isCanGetAutoInformation.value && isCanSendAutoInformationRequest.value
        )

        const isCheckLoading = computed(() => {
            if (
                state.is_person_loading ||
                state.is_company_loading ||
                state.is_person_data_loading ||
                state.is_company_data_loading ||
                state.is_auto_loading ||
                state.is_auto_data_loading
            )
                return true

            return false
        })

        const isCheckDisabled = computed(() => {
            if (state.is_person_data && !isHavePersonAccess.value) return true
            if (state.is_company_data && !isHaveCompanyAccess.value) return true
            if (state.is_auto_data && !isHaveAutoAccess.value) return true

            return false
        })

        const isSomeRequestInProgress = computed(() => {
            if (state.is_person_loading || state.is_company_loading || state.is_auto_loading)
                return true
            if (
                state.is_person_data_loading ||
                state.is_company_data_loading ||
                state.is_auto_data_loading
            )
                return true
            if (
                state.is_person_in_progress ||
                state.is_company_in_progress ||
                state.is_auto_in_progress
            )
                return true

            return false
        })

        const declineModalTitle = computed(() => {
            if (state.is_person_data) return 'Укажите причину отказа физ. лицу'
            if (state.is_company_data) return 'Укажите причину отказа юр. лицу'
            if (state.is_auto_data) return 'Укажите причину отказа авто'
            return 'title'
        })

        const removeTimers = () => {
            removePersonUpdateTimer()
            removeCompanyUpdateTimer()
            removeAutoUpdateTimer()
        }

        onBeforeUnmount(removeTimers)

        const currentType = computed(() => {
            if (state.is_person_data) return PERSON_CHECKUP_TYPE
            if (state.is_auto_data) return AUTO_CHECKUP_TYPE
            return COMPANY_CHECKUP_TYPE
        })

        const getIdentifier = computed(() => {
            if (state.is_person_data) return state.person_iin
            if (state.is_company_data) return state.company_bin
            if (state.is_auto_data) return state.auto_number

            return null
        })

        const isHistory = computed(() => {
            return (
                getIdentifier.value &&
                (isPersonInformationRender.value ||
                    isCompanyInformationRender.value ||
                    isAutoInformationRender.value)
            )
        })

        return {
            before_checked_state,
            //
            state,
            state_form$,
            //
            onByCompany,
            onByPerson,
            onByAuto,
            onCreateInformationRequest,
            //
            onConfirmPerson,
            onConfirmCompany,
            onConfirmAuto,
            //
            accept_state,
            decline_state,
            decline_form$,
            openDecline,
            closeDecline,
            openAccept,
            closeAccept,
            declineManager,
            acceptManager,
            //
            isCanDecline,
            isCanConfirm,
            isInformationHandlerRender,
            isPersonInformationRender,
            isCompanyInformationRender,
            isAutoInformationRender,
            isHaveCompanyAccess,
            isHavePersonAccess,
            isHaveAutoAccess,
            isCheckLoading,
            isCheckDisabled,
            isSomeRequestInProgress,
            declineModalTitle,
            currentType,
            isHistory,
            getIdentifier,
        }
    },
})
